import { Link } from "react-router-dom";
import { ArrowRight, Cross, HeartHandshake, Church, Flame, Sprout, CloudSun, Sunrise, BookOpen, Baby, Users } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, LineReveal, Stagger, StaggerItem } from "@/components/Reveal";
import { RESOURCES } from "@/data/resources";
import { ARTICLES } from "@/data/articles";
import { CHURCH_LOCAL_LIST } from "@/data/churchesLocal";
import { PILLARS } from "@/data/articles";

export const RESOURCE_ICONS = { Cross, HeartHandshake, Church, Flame, Sprout, CloudSun, Sunrise, BookOpen, Baby, Users };

const BASE = "https://vcclaychurch.com";

// Existing short-read articles (resources.js) and new pillar articles (articles.js),
// organized under the five content pillars.
const R = (slug) => {
  const r = RESOURCES[slug];
  return { path: `/resources/${r.slug}`, title: r.title, blurb: r.short, icon: r.icon };
};
const A = (slug, blurb) => {
  const a = ARTICLES[slug];
  return { path: `/${a.slug}`, title: a.metaTitle, blurb, icon: "BookOpen" };
};

const PILLAR_CONTENT = {
  "find-a-church": {
    links: [
      { path: "/churches-in-clay-county-fl", title: "Churches in Clay County, FL", blurb: "What to look for in a church home - and an honest introduction to Clay County's newest church.", icon: "Church" },
      R("why-come-to-church"),
    ],
    note: "city-guides",
  },
  "find-jesus": {
    links: [
      A("who-is-jesus", "His claims, His cross, His resurrection - and why your response to Him matters most."),
      A("how-to-follow-jesus", "Repent, believe, and begin - a plain-language guide to actually starting."),
      A("what-does-it-mean-to-be-saved", "Saved from what, by whom, and how it happens - in plain language."),
      A("new-christian", "Just said yes to Jesus? What happened, what's normal, and your first steps."),
      A("what-is-baptism", "The first public step of faith - what it means and who it's for."),
      A("can-god-forgive-me", "Whatever is in your past, the Bible's answer is clearer than you think. Yes - and here's why."),
      R("what-is-a-christian"),
      R("how-to-accept-christ"),
      R("baptism-of-the-holy-spirit"),
    ],
  },
  "find-hope": {
    links: [
      A("anxiety-and-the-bible", "Does God care that you're anxious? Is anxiety a sin? Honest biblical answers for anxious hearts."),
      R("overcoming-worry-and-anxiety"),
      R("overcoming-depression"),
    ],
  },
  "grow-in-faith": {
    links: [
      A("get-closer-to-god", "Feel far from God? How closeness with Him actually works - in plain language."),
      A("how-to-pray", "No fancy words required. A simple, honest guide to talking with God."),
      R("grow-closer-to-god"),
    ],
  },
  "family-life": {
    links: [
      { path: "/kids", title: "VC Kids", blurb: "A safe, joyful nursery and children's church where your kids will learn to know Jesus.", icon: "Baby" },
      { path: "/lifeline-youth", title: "Lifeline Youth Group", blurb: "A place for students to belong, grow, and encounter God for real.", icon: "Users" },
      { path: "/in-home", title: "Clay Groups", blurb: "Bible study and real community, in living rooms across Clay County.", icon: "HeartHandshake" },
    ],
    note: "More family articles are on the way - marriage, parenting, and purpose.",
  },
};

export default function Resources() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
          { "@type": "ListItem", position: 2, name: "Resources" },
        ],
      },
      {
        "@type": "CollectionPage",
        name: "Free Resources - Biblical Answers for Real Life",
        description: "Biblical resources from Victory Chapel Clay: find a church, find Jesus, find hope, grow in faith, and wisdom for family and everyday life.",
        url: `${BASE}/resources`,
        isPartOf: { "@type": "WebSite", name: "Victory Chapel Clay", url: BASE },
      },
    ],
  };

  return (
    <>
      <SEO
        title="Resources - Biblical Answers for Real Life"
        description="Free biblical resources from Victory Chapel Clay County, FL - find a church, find Jesus, find hope, grow in faith, and get wisdom for family and everyday life."
        path="/resources"
        jsonLd={jsonLd}
      />

      <section className="grain relative overflow-hidden bg-navy pb-16 pt-14 text-white sm:pb-20 sm:pt-20" data-testid="resources-hero">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-navy-light/50 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-40 -left-40 h-[420px] w-[420px] rounded-full bg-victory/20 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="flex items-center gap-2 font-display text-sm font-700 uppercase tracking-[0.3em] text-rose-300">
              <BookOpen size={16} aria-hidden="true" /> Free Resources
            </p>
          </Reveal>
          <LineReveal
            className="mt-4 font-display text-4xl font-black leading-[1.05] tracking-tighter sm:text-6xl lg:text-7xl"
            lines={["REAL QUESTIONS.", "BIBLICAL ANSWERS."]}
            delay={0.15}
          />
          <Reveal delay={0.3}>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/75 sm:text-lg">
              Whether you're searching for a church, searching for God, or just searching for hope -
              these were written for you. Pastoral resources from the team at Victory Chapel Clay.
              Take what you need, share what helps, and reach out anytime.
            </p>
          </Reveal>
        </div>
      </section>

      {Object.entries(PILLARS).map(([key, p], idx) => {
        const Icon = RESOURCE_ICONS[p.icon] || BookOpen;
        const content = PILLAR_CONTENT[key];
        return (
          <section key={key} className={`${idx % 2 === 0 ? "bg-cream" : "bg-white"} py-14 sm:py-20`} data-testid={`pillar-${key}`}>
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
              <Reveal>
                <div className="flex items-start gap-4">
                  <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-navy text-white">
                    <Icon size={26} aria-hidden="true" />
                  </span>
                  <div>
                    <h2 className="font-display text-3xl font-black tracking-tighter text-navy sm:text-4xl">{p.name.toUpperCase()}</h2>
                    <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">{p.blurb}</p>
                  </div>
                </div>
              </Reveal>
              <Stagger className="mt-8 grid gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
                {content.links.map((l) => {
                  const CardIcon = RESOURCE_ICONS[l.icon] || BookOpen;
                  return (
                    <StaggerItem key={l.path}>
                      <Link to={l.path} data-testid={`pillar-card-${l.path.replace(/\//g, "-")}`}
                            className="group flex h-full flex-col rounded-3xl border border-navy/10 bg-white p-7 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift">
                        <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-navy/5 text-navy transition-colors group-hover:bg-victory group-hover:text-white">
                          <CardIcon size={22} aria-hidden="true" />
                        </span>
                        <h3 className="mt-5 font-display text-xl font-800 tracking-tight text-navy">{l.title}</h3>
                        <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{l.blurb}</p>
                        <span className="mt-5 inline-flex items-center gap-1.5 font-display text-xs font-700 text-victory">
                          READ <ArrowRight size={13} aria-hidden="true" className="transition-transform group-hover:translate-x-1" />
                        </span>
                      </Link>
                    </StaggerItem>
                  );
                })}
                {key === "find-a-church" && (
                  <StaggerItem>
                    <div className="flex h-full flex-col rounded-3xl bg-navy p-7 text-white" data-testid="pillar-card-city-guides">
                      <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-victory text-white">
                        <Church size={22} aria-hidden="true" />
                      </span>
                      <h3 className="mt-5 font-display text-xl font-800 tracking-tight">Church Near Your Community</h3>
                      <div className="mt-3 flex flex-1 flex-wrap content-start gap-2">
                        {CHURCH_LOCAL_LIST.map((c) => (
                          <Link key={c.slug} to={`/${c.slug}`} data-testid={`pillar-city-${c.slug}`}
                                className="rounded-full border border-white/25 px-4 py-1.5 text-xs font-semibold text-white/85 transition-colors hover:bg-white/10 hover:text-white">
                            {c.city}
                          </Link>
                        ))}
                      </div>
                      <span className="mt-5 font-display text-xs font-700 text-rose-300">LOCAL GUIDES FOR EACH TOWN</span>
                    </div>
                  </StaggerItem>
                )}
              </Stagger>
              {content.note && content.note !== "city-guides" && (
                <p className="mt-6 text-sm italic text-slate-500">{content.note}</p>
              )}
            </div>
          </section>
        );
      })}

      <section className="bg-cream pb-16 sm:pb-24" data-testid="resources-ask-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="rounded-3xl bg-navy p-8 text-white shadow-lift sm:p-10" data-testid="resource-card-ask">
              <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
                <div className="max-w-xl">
                  <h2 className="font-display text-2xl font-800 tracking-tight sm:text-3xl">A QUESTION WE DIDN'T COVER?</h2>
                  <p className="mt-2 text-sm leading-relaxed text-white/70 sm:text-base">
                    No question is too big, too small, or too honest. Ask us anything - or simply ask for prayer.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2.5">
                  <Link to="/contact" data-testid="resources-contact-cta"
                        className="inline-flex items-center gap-1.5 rounded-full bg-victory px-6 py-3 font-display text-sm font-700 text-white transition-colors hover:bg-victory-dark">
                    CONTACT US <ArrowRight size={14} aria-hidden="true" />
                  </Link>
                  <Link to="/prayer" data-testid="resources-pray-cta"
                        className="inline-flex items-center gap-1.5 rounded-full border border-white/30 px-6 py-3 font-display text-sm font-700 text-white transition-colors hover:bg-white/10">
                    REQUEST PRAYER
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
